import { RARITIES, RARITY_COLORS, type Rarity } from '@d2/core';
import { type GroundKind } from '@d2/schema';

import { PALETTE } from './art-spec';

/**
 * What a drop looks like — decided here, drawn elsewhere, and Phaser-free so
 * both halves can be tested without a browser.
 *
 * `monster-art.ts`'s counterpart for the fourth thing a room replicates, and the
 * same division of labour: `art-spec.ts` holds the numbers and the colours,
 * `placeholder-art.ts` turns them into a texture, and this is the mapping from
 * *what the wire says a drop is* to *how it is drawn*.
 *
 * ## No rule crosses this file either
 *
 * The wire sends a kind code and a rarity code and `./wire` turns both back into
 * ids — *"a reference, not a table"*. What a tier is *worth* is core's, what it
 * costs is the API's, and neither is asked for here. The one thing this module
 * does know is what a tier looks like, and even that is not typed out: the six
 * hexes are `docs/GAME-DESIGN.md`'s, and core's `RARITY_COLORS` is where the
 * design doc transcribed them, so they are read from there rather than copied.
 *
 * One shape for every drop, and colour is the whole of the distinction. That is
 * a placeholder-art decision (see `GROUND_ITEM_TEXTURE`) and not a claim about
 * the game: it is exactly what a player reads off a D2 floor before deciding to
 * walk over, which is why the design doc gives the tiers a palette in the first
 * place.
 */

/**
 * The tiers, as Phaser colour numbers, built from core's own table.
 *
 * A `Map` rather than a `Record<Rarity, number>` because the id arriving off the
 * wire is a `string`: the wire's tier list is append-only and core's is core's,
 * and a client one release behind meets an id it cannot name. A lookup that can
 * answer "I have never heard of that" is the shape of that fact; a `Record`
 * would need a cast to be asked the question at all.
 */
const RARITY_FILL: ReadonlyMap<string, number> = new Map(
  RARITIES.map((rarity: Rarity) => [rarity, Number.parseInt(RARITY_COLORS[rarity].slice(1), 16)]),
);

/** The colour of a tier, or `null` for one this client cannot name — including "no tier at all". */
export const rarityFill = (rarity: string | null): number | null =>
  rarity === null ? null : (RARITY_FILL.get(rarity) ?? null);

/**
 * The colour of the kinds that are not on the rarity axis at all.
 *
 * A potion and a pile of coins are not "normal quality" — `GroundItemState`
 * gives all three of them `NO_RARITY` precisely because the tier does not apply.
 * `gear` is in the table as the fallback for an entry that arrived with a tier
 * this client cannot name, which is a room disagreeing with a client rather than
 * anything a player did.
 */
const KIND_FILL: Readonly<Record<GroundKind, number>> = {
  gear: PALETTE.groundItem,
  potion: PALETTE.dropPotion,
  gold: PALETTE.dropGold,
  respec: PALETTE.dropRespec,
};

/** The one thing the renderer asks about a drop: what colour to tint it. */
export const groundFill = (item: { readonly kind: GroundKind; readonly rarity: string | null }): number =>
  rarityFill(item.rarity) ?? KIND_FILL[item.kind];
